import {registerBrick, Auth, CloudObject, User} from 'olympe';
import {ReactBrick, useProperty, parseCurrentHash} from "@olympeio/core";
import {combineLatest} from "rxjs";
import React, { useState, useEffect } from 'react';
import {
    Heart,
    Home,
    Calendar,
    Users,
    FileText,
    Bell,
    Search,
    X,
    CalendarRange,
    TvMinimalPlay,
    MessageCircleMore,
    UserRoundCog,
    Menu, LifeBuoy, Earth, HeartHandshake, ThumbsUp, Lock
} from 'lucide-react';
import {Box} from '@mui/material';

export default class DarkResponsiveLayout extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('menu entries', true),
        ]);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden, menuEntries] = props.values;

            const getIconComponent = (iconName) => {
                const icons = {
                    Heart,
                    Home,
                    Calendar,
                    CalendarRange,
                    TvMinimalPlay,
                    MessageCircleMore,
                    Users,
                    FileText,
                    Bell,
                    UserRoundCog,
                    LifeBuoy,
                    Earth,
                    HeartHandshake,
                    ThumbsUp,
                    Lock
                };
                return icons[iconName] || Home;
            };

            const [menu, setMenu] = useState([]);
            const [isOpen, setIsOpen] = useState(true);
            const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
            const [activeItem, setActiveItem] = useState(parseCurrentHash());
            const [isMobile, setIsMobile] = useState(false);
            const [boxEl, setBoxEl] = React.useState(null);

            const renderer = useProperty($, 'renderer', true);
            const hasNotifications = useProperty($, 'has notifications', true);

            useEffect(() => {
                setMenu(menuEntries?.toArray().map(a => a.toJSON()));
            }, [menuEntries]);

            useEffect(() => {
                const checkMobile = () => {
                    setIsMobile(window.innerWidth < 768);
                    if (window.innerWidth < 768) {
                        setIsOpen(false);
                    }
                };

                checkMobile();
                window.addEventListener('resize', checkMobile);
                return () => window.removeEventListener('resize', checkMobile);
            }, []);

            // Create and destroy runner using useEffect
            React.useEffect(() => {
                let renderer$;
                if (boxEl && !hidden && renderer) {
                    renderer$ = $.runner(renderer).setParentElement(boxEl);
                    const onChange = () => {
                        renderer$.set('Width', boxEl.getBoundingClientRect().width);
                        renderer$.set('Height', boxEl.getBoundingClientRect().height);
                    }
                    new ResizeObserver(onChange).observe(boxEl);
                }
                return () => {
                    renderer$?.destroy();
                };
            }, [boxEl, renderer, hidden]);

            const getSlang = () => {
                const fullId = CloudObject.get(Auth.getCurrentUser()).get(User.SAMLnameIdProp);
                if (fullId) {
                    let parts = fullId.split('.');
                    if (parts.length < 2) {
                        parts = fullId.split(' ');
                    }
                    if (parts.length < 2) {
                        parts = [fullId[0], fullId[1]]
                    }
                    return parts.map(a => a[0].toUpperCase()).join('');
                } else {
                    return '';
                }
            }

            const toggleMenu = () => {
                if (isMobile) {
                    setIsMobileMenuOpen(!isMobileMenuOpen);
                } else {
                    setIsOpen(!isOpen);
                }
            };

            const handleMenuClick = (item) => {
                const lambda = $.get('on menu click');
                const [ctrlFlow, menuEntry] = lambda.getInputs();
                const runner$ = $.runner(lambda)
                    .set(menuEntry, item)
                    .set(ctrlFlow, new Date());
                $.onDestroy(() => runner$.destroy());

                setActiveItem(item.path);
                if (isMobile) {
                    setIsMobileMenuOpen(false);
                }
            };

            const MenuItems = ({ mobile = false }) => (
                <div className={`py-6 ${mobile ? 'px-6' : 'px-3'}`}>
                    {menu?.sort((a, b) => a.rank - b.rank).map((item, idx) => {
                        const Icon = getIconComponent(item.icon);
                        return (
                            <div
                                key={item.path}
                                className={`relative my-1 ${!mobile && isOpen ? 'px-2' : 'px-1'}`}
                            >
                                <button
                                    onClick={() => handleMenuClick(item)}
                                    className={`
                                        w-full flex items-center py-3 px-3 rounded-xl
                                        transition-all duration-200 group
                                        ${activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/'))
                                        ? 'bg-green-500/10 text-green-400'
                                        : 'hover:bg-gray-800/50 hover:text-green-400'}   
                                    `}
                                >
                                    <Icon
                                        size={20}
                                        className={`transition-transform group-hover:scale-110 ${
                                            activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/')) ? 'text-green-400' : ''
                                        }`}
                                    />
                                    {(mobile || isOpen) && (
                                        <span className="ml-3 truncate font-medium">
                                            {item.description}
                                        </span>
                                    )}
                                    {(activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/'))) && !mobile && (
                                        <div className="absolute left-0 w-1 h-8 bg-green-400 rounded-r-full" />
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            );


            return !hidden && (
                <div className="flex flex-col h-screen bg-black text-gray-100">
                    {/* Header */}
                    <header className="h-16 bg-gray-900/50 backdrop-blur-md border-b border-gray-800 px-6 flex items-center justify-between z-20">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={toggleMenu}
                                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <Menu size={20} className="text-green-400" />
                            </button>
                            <h1 className="text-xl font-bold bg-gradient-to-r from-green-400 to-green-200 bg-clip-text text-transparent">
                                {useProperty($, 'title', true)}
                            </h1>
                        </div>

                        <div className="flex items-center space-x-6">
                            <div className="relative hidden md:block">
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className="bg-gray-800/50 w-64 px-4 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-all"
                                />
                                <Search size={18} className="absolute right-3 top-2.5 text-gray-500" />
                            </div>
                            <button className="relative p-2 hover:bg-gray-800 rounded-lg transition-colors" onClick={() => $.trigger('on notifications click')}>
                                <Bell size={20} />
                                {hasNotifications && (<span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full" />)}
                            </button>
                            <div className="h-8 w-8 bg-gray-800 rounded-full ring-2 ring-green-500/50 flex items-center justify-center" onClick={() => $.trigger('on account click')}>
                                <span className="font-bold text-white">{getSlang()}</span>
                            </div>
                        </div>
                    </header>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Desktop Sidebar */}
                        {!isMobile && (
                            <div
                                className={`relative transition-all duration-300 ease-in-out ${
                                    isOpen ? 'w-64' : 'w-20'
                                } bg-gray-900/30 backdrop-blur-sm border-r border-gray-800`}
                            >
                                <MenuItems />
                            </div>
                        )}

                        {/* Mobile Menu Overlay */}
                        {isMobile && isMobileMenuOpen && (
                            <div className="fixed inset-0 bg-black/50 w-full backdrop-blur-sm z-30">
                                <div className="fixed inset-y-0 left-0 w-full bg-gray-900 shadow-lg z-40">
                                    <div className="flex items-center justify-between p-4 border-b border-gray-800">
                                        <h2 className="text-xl font-semibold text-green-400">Menu</h2>
                                        <button
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                                        >
                                            <X size={20} className="text-green-400" />
                                        </button>
                                    </div>
                                    <MenuItems mobile={true} />
                                </div>
                            </div>
                        )}

                        {/* Main Content */}
                        <div className="flex-1 overflow-auto bg-gradient-to-br from-gray-900 to-black" style={{position: 'relative'}}>
                            <Box
                                ref={setBoxEl}
                                sx={{
                                    width: 1,
                                    height: 1,
                                    boxSizing: 'border-box',
                                    position: 'absolute',
                                    overflow: useProperty($, 'Allow Content Overflow') ? 'visible' : 'hidden'
                                }}
                            />
                        </div>
                    </div>
                </div>
            );
        }
    }
}

registerBrick('0193154cb451d1e7cc99', DarkResponsiveLayout);
